import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Check,
  X,
  Send,
  Ban,
  Loader2
} from 'lucide-react';
import purchaseService from '../services/purchaseService';

const PurchaseApprovalActions = ({
  order,
  onApprovalChange,
  userRole = 'user',
  variant = 'contained',
  size = 'medium'
}) => {
  const [actionDialog, setActionDialog] = useState({
    open: false,
    action: '',
    reason: '',
    loading: false,
    error: ''
  });

  const canApprove = userRole === 'admin' || userRole === 'manager';
  const canSubmit = order?.status === 'draft';
  const canCancel = order?.status !== 'cancelled' && order?.fulfillment_status === 'pending';

  const handleAction = (action) => {
    setActionDialog({
      open: true,
      action,
      reason: '',
      loading: false,
      error: ''
    });
  };

  const handleConfirmAction = async () => {
    const { action, reason } = actionDialog;

    setActionDialog(prev => ({ ...prev, loading: true, error: '' }));

    try {
      let result;

      switch (action) {
        case 'approve':
          result = await purchaseService.approvePurchaseOrder(order._id);
          break;
        case 'reject':
          if (!reason.trim()) {
            setActionDialog(prev => ({
              ...prev,
              error: '请输入拒绝原因',
              loading: false
            }));
            return;
          }
          result = await purchaseService.rejectPurchaseOrder(order._id, reason);
          break;
        case 'submit':
          result = await purchaseService.submitPurchaseOrder(order._id);
          break;
        case 'cancel':
          result = await purchaseService.cancelPurchaseOrder(order._id);
          break;
        default:
          throw new Error('Unknown action');
      }

      setActionDialog({
        open: false,
        action: '',
        reason: '',
        loading: false,
        error: ''
      });

      if (onApprovalChange) {
        const normalized = (result && result.order) ? result.order : result;
        onApprovalChange(normalized);
      }

    } catch (error) {
      console.error(`Error ${action}ing purchase order:`, error);
      setActionDialog(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || `Failed to ${action} purchase order`
      }));
    }
  };

  const handleCloseDialog = () => {
    if (!actionDialog.loading) {
      setActionDialog({
        open: false,
        action: '',
        reason: '',
        loading: false,
        error: ''
      });
    }
  };

  const getActionTitle = (action) => {
    switch (action) {
      case 'approve': return '批准采购单';
      case 'reject': return '拒绝采购单';
      case 'submit': return '提交采购单';
      case 'cancel': return '取消采购单';
      default: return '确认操作';
    }
  };

  const getActionMessage = (action) => {
    switch (action) {
      case 'approve':
        return `确定要批准采购单 ${order?.purchase_order_id} 吗？`;
      case 'reject':
        return `确定要拒绝采购单 ${order?.purchase_order_id} 吗？`;
      case 'submit':
        return `确定要提交采购单 ${order?.purchase_order_id} 进行审批吗？`;
      case 'cancel':
        return `确定要取消采购单 ${order?.purchase_order_id} 吗？此操作不可撤销。`;
      default:
        return '确定要执行此操作吗？';
    }
  };

  const getActionVariant = (action) => {
    switch (action) {
      case 'approve': return 'default';
      case 'reject': return 'destructive';
      case 'cancel': return 'destructive';
      default: return 'default';
    }
  };

  if (!order) {
    return null;
  }

  const buttonSize = size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'default';

  return (
    <>
      <div className="flex gap-2 flex-wrap">
        {/* Submit for Approval */}
        {canSubmit && (
          <Button
            size={buttonSize}
            onClick={() => handleAction('submit')}
          >
            <Send className="h-4 w-4 mr-2" />
            提交审批
          </Button>
        )}

        {/* Approval Actions (Admin/Manager only) */}
        {canApprove && order?.approval_status === 'pending' && (
          <div className="flex">
            <Button
              size={buttonSize}
              onClick={() => handleAction('approve')}
              className="rounded-r-none bg-success hover:bg-success"
            >
              <Check className="h-4 w-4 mr-2" />
              批准
            </Button>
            <Button
              size={buttonSize}
              variant="destructive"
              onClick={() => handleAction('reject')}
              className="rounded-l-none"
            >
              <X className="h-4 w-4 mr-2" />
              拒绝
            </Button>
          </div>
        )}

        {/* Cancel Action */}
        {canCancel && (
          <Button
            variant="outline"
            size={buttonSize}
            onClick={() => handleAction('cancel')}
            className="text-destructive border-red-300 hover:bg-destructive/10"
          >
            <Ban className="h-4 w-4 mr-2" />
            取消订单
          </Button>
        )}
      </div>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={handleCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getActionTitle(actionDialog.action)}</DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {actionDialog.error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{actionDialog.error}</AlertDescription>
              </Alert>
            )}

            <p className="text-sm text-muted-foreground mb-4">
              {getActionMessage(actionDialog.action)}
            </p>

            {actionDialog.action === 'reject' && (
              <div className="space-y-2">
                <Label htmlFor="reason">拒绝原因 *</Label>
                <Textarea
                  id="reason"
                  rows={4}
                  value={actionDialog.reason}
                  onChange={(e) => setActionDialog(prev => ({
                    ...prev,
                    reason: e.target.value,
                    error: ''
                  }))}
                  placeholder="请详细说明拒绝原因..."
                  disabled={actionDialog.loading}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseDialog}
              disabled={actionDialog.loading}
            >
              取消
            </Button>
            <Button
              onClick={handleConfirmAction}
              variant={getActionVariant(actionDialog.action)}
              disabled={
                actionDialog.loading ||
                (actionDialog.action === 'reject' && !actionDialog.reason.trim())
              }
              className={actionDialog.action === 'approve' ? 'bg-success hover:bg-success' : ''}
            >
              {actionDialog.loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {actionDialog.loading ? '处理中...' : '确认'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PurchaseApprovalActions;
